import React, { useState, useEffect, useMemo } from "react";
import { Link, useParams, useSearchParams, useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import SafeLink from "../../components/SafeLink";
import Rating from "../../components/Rating";
import RatingModal from "../../components/RatingModal";
import { fetchToolsByCategory } from "../../services/apiService";
import { useAuth } from "../../context/AuthContext";
import {
  Search,
  ArrowUpDown,
  Filter,
  Zap,
  ShieldCheck,
  Plus,
  Check,
  ChevronLeft,
  LayoutGrid,
  TrendingUp,
  Database,
  ExternalLink,
  BarChart3,
  DollarSign,
  Activity,
  Coins,
  Bookmark,
  Share2,
  X,
  Users
} from "lucide-react";
import { useBookmarks } from "../../hooks/useBookmarks";
import { CardSkeleton } from "../../components/Skeleton";
import MetricsPanel from "../../components/MetricsPanel";
import ToolLogo from "../../components/ToolLogo";
import ChainFilter from "../../components/ChainFilter";
import { useMetrics } from "../../context/MetricsContext";



const fmt = (n) => n > 0 ? '$' + new Intl.NumberFormat('en-US', { notation: 'compact', compactDisplay: 'short', maximumFractionDigits: 2 }).format(n) : '—';

// ── Animation Variants ──
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.07, delayChildren: 0.1 }
  }
};

const itemVariants = {
  hidden: { y: 30, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: { type: "spring", stiffness: 80, damping: 18 }
  }
};

const fadeUp = {
  hidden: { y: 40, opacity: 0 },
  visible: (delay = 0) => ({
    y: 0,
    opacity: 1,
    transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1], delay }
  })
};

const slideRight = {
  hidden: { x: -60, opacity: 0 },
  visible: (delay = 0) => ({
    x: 0,
    opacity: 1,
    transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1], delay }
  })
};

const scaleIn = {
  hidden: { scale: 0.9, opacity: 0 },
  visible: (delay = 0) => ({
    scale: 1,
    opacity: 1,
    transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1], delay }
  })
};

const CATEGORY_META = {
  // --- Live categories ---
  trading:     { title: 'Trading & Exchanges',      desc: 'Decentralized spot exchanges (DEXs), automated market makers (AMMs), and decentralized perpetual futures trading.', color: 'purple',  comingSoon: false },
  bridges:     { title: 'Bridges & Interoperability', desc: 'Cross-chain bridging protocols, arbitrary messaging layers, and liquidity routers powering multi-chain asset transfers.', color: 'blue',    comingSoon: false },
  defi:        { title: 'Decentralized Finance (DeFi)', desc: 'Non-custodial lending markets, algorithmic money markets, yield optimizers, and decentralized stablecoin issuers.', color: 'emerald', comingSoon: false },
  rwa:         { title: 'Real World Assets (RWA)', desc: 'Tokenized US Treasury bills, institutional credit pools, real estate syndicates, and private equity protocol infrastructure.', color: 'amber',   comingSoon: false  },
  security:    { title: 'Security & Audits',        desc: 'Smart contract audit platforms, real-time transaction simulation engines, approval revoking utilities, and bug bounty hubs.', color: 'red',     comingSoon: false },
  analytics:   { title: 'Analytics & Intelligence', desc: 'On-chain portfolio tracking, whale movement monitors, DEX liquidity analytics, and blockchain intelligence dashboards.', color: 'cyan',    comingSoon: false },
  wallets:     { title: 'Wallets & Infrastructure', desc: 'Self-custodial Web3 wallets, multi-signature vaults, passkey smart accounts, and account abstraction infrastructure.', color: 'slate',   comingSoon: false },
  cex:         { title: 'Centralized Exchanges (CEX)', desc: 'Global fiat gateways, spot trading venues, proof-of-reserves exchanges, and institutional custody platforms.', color: 'yellow', comingSoon: false },
  gaming:      { title: 'Web3 Gaming & GameFi',     desc: 'Play-and-earn games, metaverse virtual worlds, fully on-chain gaming engines, and gaming guild ecosystems.', color: 'green',   comingSoon: false },
  privacy:     { title: 'Privacy & Zero-Knowledge', desc: 'Zero-knowledge proof systems, confidential smart contracts, private transaction networks, and stealth address tools.', color: 'gray',    comingSoon: false  },
  predictions: { title: 'Prediction Markets & InfoFi', desc: 'Decentralized prediction markets, outcome betting pools, social attention indexes, and sentiment trading protocols.', color: 'orange',  comingSoon: false  },
  community:   { title: 'Onchain Tools & Utilities', desc: 'Web3 developer toolkits, contract safety engines, block explorers, and open-source infrastructure.', color: 'teal',    comingSoon: false },
  infra:       { title: 'Infra & Dev Tools',        desc: 'RPC infrastructure, node providers, indexers, and developer SDKs for Web3 applications.', color: 'slate',   comingSoon: true  },
  ai:          { title: 'AI & Autonomous Agents',   desc: 'Decentralized AI inference networks, autonomous AI agent frameworks, sentiment engines, and compute markets.', color: 'indigo', comingSoon: true  },
  social:      { title: 'Social & DeSoc',           desc: 'Decentralized social graphs, token-gated community hubs, Web3 messaging protocols, and identity systems.', color: 'blue',    comingSoon: true  },
  dao:         { title: 'DAO',                      desc: 'Decentralized governance frameworks, multi-sig proposal voting portals, and treasury management platforms.', color: 'purple',  comingSoon: false },
  depin:       { title: 'DePIN',                    desc: 'Decentralized Physical Infrastructure Networks including storage, wireless, and compute resources.', color: 'orange',  comingSoon: true  },
  staking:     { title: 'Staking & Liquid Staking', desc: 'Liquid staking tokens (LSTs), liquid restaking protocols (LRTs), validator pools, and yield strategies.', color: 'yellow',  comingSoon: true  },
  payments:    { title: 'Payments & Settlement',    desc: 'Crypto payroll, cross-border remittance, merchant payment gateways, and invoicing platforms.', color: 'emerald', comingSoon: true  },
  airdrops:    { title: 'Airdrops & Reward Hubs',   desc: 'Verified reward campaigns, testnet incentive programs, and active ecosystem points initiatives.', color: 'yellow', comingSoon: false },
  'bounty-hub': { title: 'Bounty Hub & Freelance',   desc: 'Developer bounties, security audit contests, design challenges, and freelance Web3 task marketplaces.', color: 'indigo',  comingSoon: false },
  // --- Alias support ---
  dex:              { title: 'Trading & Exchanges',      desc: 'Decentralized spot exchanges (DEXs), automated market makers (AMMs), and decentralized perpetual futures trading.', color: 'purple',  comingSoon: false },
  perps:            { title: 'Trading & Exchanges',      desc: 'Decentralized spot exchanges (DEXs), automated market makers (AMMs), and decentralized perpetual futures trading.', color: 'purple',  comingSoon: false },
  interoperability: { title: 'Bridges & Interoperability', desc: 'Cross-chain communication protocols and multi-chain bridges.', color: 'blue',    comingSoon: false },
  infofi:           { title: 'InfoFi',                 desc: 'On-chain research, sentiment analytics, and information markets.', color: 'purple',  comingSoon: false },
  bountyHub:        { title: 'Bounty Hub & Freelance',   desc: 'Developer bounties, security audit contests, design challenges, and freelance Web3 task marketplaces.', color: 'indigo',  comingSoon: false },
  communityTools:   { title: 'Onchain Tools & Utilities', desc: 'Web3 developer toolkits, contract safety engines, block explorers, and open-source infrastructure.', color: 'teal',    comingSoon: false },
  'onchain-tools':  { title: 'Onchain Tools & Utilities', desc: 'Web3 developer toolkits, contract safety engines, block explorers, and open-source infrastructure.', color: 'teal',    comingSoon: false },
  onchainTools:     { title: 'Onchain Tools & Utilities', desc: 'Web3 developer toolkits, contract safety engines, block explorers, and open-source infrastructure.', color: 'teal',    comingSoon: false },
};

const heroImages = {
  dex: '/images/heroes/hero-dex.png',
  perps: '/images/heroes/hero-perps.png',
  web3Chat: '/images/heroes/hero-perps.png',
  interoperability: '/images/heroes/hero-interoperability.png',
  infofi: '/images/heroes/hero-community-tools.png',
  communityTools: '/images/heroes/hero-community-tools.png',
  bountyHub: '/images/heroes/hero-community-tools.png',
};

export default function CategoryPage({ categoryKey: propCategoryKey, title: propTitle, description: propDescription }) {
  const params = useParams();
  const location = useLocation();
  const categoryKey = propCategoryKey || params.categoryKey;
  const meta = CATEGORY_META[categoryKey] || {};
  const title = propTitle || meta.title || categoryKey;
  const description = propDescription || meta.desc || '';
  const isComingSoon = meta.comingSoon === true;
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const autoOpenAppId = searchParams.get('rate');

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { selectedChain } = useMetrics();
  const [selectedTool, setSelectedTool] = useState(null);
  const [selectedMetricsProtocol, setSelectedMetricsProtocol] = useState(null);
  const { toggleBookmark, isBookmarked } = useBookmarks();
  const [daoApplicationTarget, setDaoApplicationTarget] = useState(null);


  // Filters & Search State
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("default");
  const [chainFilter, setChainFilter] = useState("all");
  const [tradingSubFilter, setTradingSubFilter] = useState("all"); // 'all' | 'dex' | 'perps'
  const [networkScope, setNetworkScope] = useState("all"); // 'all' | 'mainnet' | 'testnet'

  // Comparison Bench State (Synced with URL for ToolComparison compatibility)
  const [bench, setBench] = useState([]);

  useEffect(() => {
    const tools = searchParams.get("tools")?.split(",").filter(Boolean) || [];
    setBench(tools);
  }, [searchParams]);

  const toggleBench = (toolId) => {
    const strId = String(toolId);
    let newBench;
    if (bench.includes(strId)) {
      newBench = bench.filter(id => id !== strId);
    } else {
      if (bench.length >= 4) return;
      newBench = [...bench, strId];
    }
    setBench(newBench);
    setSearchParams(prev => {
      if (newBench.length > 0) {
        prev.set("tools", newBench.join(","));
      } else {
        prev.delete("tools");
      }
      return prev;
    });
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const toolsData = await fetchToolsByCategory(categoryKey);
        const activeTools = toolsData.filter(tool => tool.status === 'active' || !tool.status);
        setData(activeTools);
        setError(null);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (categoryKey) fetchData();
  }, [categoryKey]);

  // ── Auto-open MetricsPanel when navigated from Notifications or Home ──
  useEffect(() => {
    const id = searchParams.get('openTool') || searchParams.get('tool') || searchParams.get('id') || location.state?.openToolId;
    if (id && !loading && data.length > 0) {
      const match = data.find(t => (t.id || t._id) === id || String(t.id) === String(id) || String(t._id) === String(id) || (t.name && t.name.toLowerCase() === String(id).toLowerCase()));
      if (match) {
        setSelectedMetricsProtocol({
          ...match,
          id: match.id || match._id,
          slug: match.llamaSlug || match.slug || match.id,
          rating: match.averageRating || match.rating,
        });
        // Highlight element
        setTimeout(() => {
          const el = document.getElementById(`tool-${match.id || match._id}`);
          if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 300);
      }
    }
  }, [searchParams, location.state, loading, data]);

  // ── App Store derived data ──
  const isFiltered = searchQuery || chainFilter !== 'all' || sortBy !== 'default' || tradingSubFilter !== 'all' || selectedChain !== 'All' || networkScope !== 'all';

  // Featured = highest rated (or first if no ratings)
  const featured = useMemo(() => {
    if (isFiltered) return null;
    let filtered = [...data];
    if (selectedChain !== 'All') {
      filtered = filtered.filter(t => t.metrics?.chains?.some(c => c.toLowerCase() === selectedChain.toLowerCase()));
    }
    const sorted = filtered.sort((a, b) => (b.averageRating || b.rating || 0) - (a.averageRating || a.rating || 0));
    return sorted[0] || null;
  }, [data, isFiltered, selectedChain]);

  // Trending = next 4 by TVL
  const trending = useMemo(() => {
    if (isFiltered) return [];
    const rest = data.filter(t => t._id !== featured?._id);
    return [...rest].sort((a, b) => (b.metrics?.tvl || 0) - (a.metrics?.tvl || 0)).slice(0, 4);
  }, [data, featured, isFiltered]);

  // New = everything else
  const newProtocols = useMemo(() => {
    if (isFiltered) return [];
    const trendingIds = new Set(trending.map(t => t._id));
    return data.filter(t => t._id !== featured?._id && !trendingIds.has(t._id));
  }, [data, featured, trending, isFiltered]);

  // Chain pills derived from data
  const chainPills = useMemo(() => {
    const chains = new Set();
    data.forEach(t => t.metrics?.chains?.forEach(c => chains.add(c)));
    return Array.from(chains).sort().slice(0, 5);
  }, [data]);

  // Derived filtered data when search/filters are active
  const filteredData = useMemo(() => {
    let result = [...data];

    // Global Chain Filter
    if (selectedChain !== 'All') {
      result = result.filter(t => t.metrics?.chains?.some(c => c.toLowerCase() === selectedChain.toLowerCase()));
    }

    // Trading sub-filter (DEX vs Perps)
    if (categoryKey === 'trading' && tradingSubFilter !== 'all') {
      const subFilterMap = {
        dex: ['dex', 'trading'],
        perps: ['perps'],
      };
      const matchCats = subFilterMap[tradingSubFilter] || [tradingSubFilter];
      result = result.filter(t => matchCats.includes(t.category));
    }

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(t => 
        t.name?.toLowerCase().includes(q) || 
        t.description?.toLowerCase().includes(q) ||
        t.category?.toLowerCase().includes(q)
      );
    }

    if (networkScope === 'mainnet') {
      result = result.filter(t => !t.isTestnet);
    } else if (networkScope === 'testnet') {
      result = result.filter(t => t.isTestnet);
    }

    if (chainFilter !== 'all') {
      result = result.filter(t => t.metrics?.chains?.includes(chainFilter));
    }

    if (sortBy === 'rating') {
      result.sort((a, b) => (b.averageRating || b.rating || 0) - (a.averageRating || a.rating || 0));
    } else if (sortBy === 'tvl') {
      result.sort((a, b) => (b.metrics?.tvl || 0) - (a.metrics?.tvl || 0));
    }

    return result;
  }, [data, isFiltered, searchQuery, chainFilter, sortBy, categoryKey, tradingSubFilter, selectedChain, networkScope]);

  const StarRating = ({ value = 0, count }) => (
    <div className="flex items-center gap-1">
      <div className="flex gap-px">
        {[1,2,3,4,5].map(s => (
          <span key={s} className={`text-xs ${s <= Math.round(value) ? 'text-yellow-400' : 'text-gray-200'}`}>★</span>
        ))}
      </div>
      <span className="text-xs text-gray-400 font-medium">{value ? value.toFixed(1) : '—'}{count ? ` (${count})` : ''}</span>
    </div>
  );

  const ProtocolCard = ({ app }) => {
    const rating = app.averageRating || app.rating || 0;
    return (
      <motion.div
        layout
        variants={itemVariants}
        initial="hidden"
        animate="visible"
        exit={{ opacity: 0, scale: 0.9 }}
        id={`tool-${app._id || app.id}`}
        className="group h-full transition-all duration-500"
      >
        <div 
          onClick={() => setSelectedMetricsProtocol({
            ...app,
            id: app.id || app._id,
            slug: app.llamaSlug || app.slug || app.id,
            rating: app.averageRating || app.rating,
          })}
          className="bg-white border-2 border-gray-200 rounded-[1.25rem] p-4 flex flex-col hover:border-purple-300 hover:shadow-lg transition-all h-full cursor-pointer"
        >
          {/* Top row: Logo and Bookmark */}
          <div className="flex items-start justify-between mb-3">
            <div className="flex gap-2.5">
              <div className="w-[50px] h-[50px] bg-white rounded-[14px] shadow-sm border-2 border-gray-200 p-1 relative flex-shrink-0">
                <ToolLogo tool={app} />

              </div>
              
              {/* Labels/Tags */}
              <div className="flex flex-col gap-1">
                {app.hasAirdrop && (
                  <div className="flex items-center gap-1 bg-amber-50 text-amber-600 px-1.5 py-0.5 rounded-md text-[9px] font-black uppercase tracking-wider border border-amber-100/50">
                    <Star size={8} className="fill-current" /> Airdrop
                  </div>
                )}
                {app.metrics?.chains?.[0] && (
                  <div className="bg-slate-50 text-slate-500 px-1.5 py-0.5 rounded-md text-[9px] font-bold uppercase tracking-wider border border-slate-100 w-fit">
                    {app.metrics.chains[0]}
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-center gap-1.5">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  const shareUrl = `${window.location.origin}/apps/${categoryKey}?id=${app._id || app.id}`;
                  if (navigator.share) {
                    navigator.share({ title: app.name, text: app.description, url: shareUrl }).catch(() => {});
                  } else {
                    navigator.clipboard.writeText(shareUrl);
                    alert('Link copied to clipboard!');
                  }
                }}
                className="w-8 h-8 rounded-xl border bg-gray-50 border-gray-100 text-gray-400 hover:border-purple-300 hover:text-purple-600 flex items-center justify-center transition-all shrink-0"
                title="Share"
              >
                <Share2 size={13} />
              </button>
              
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (!user || user.email === 'guest@web3central.internal') { navigate('/login'); return; }
                  toggleBookmark(app);
                }}
                className={`w-8 h-8 rounded-xl border flex items-center justify-center transition-all shrink-0 ${isBookmarked(app.id || app._id)
                  ? 'bg-purple-50 border-purple-200 text-purple-600'
                  : 'bg-gray-50 border-gray-100 text-gray-400 hover:border-purple-300 hover:text-purple-600'}`}
              >
                <Bookmark size={13} className={isBookmarked(app.id || app._id) ? 'fill-current' : ''} />
              </button>
            </div>
          </div>

          {/* Name */}
          <h4 className="font-bold text-[15px] leading-tight mb-1 text-gray-900 truncate group-hover:text-purple-700 transition-colors flex items-center gap-1">
            {app.name}
            {app.verified && <ShieldCheck size={12} className="text-emerald-500 shrink-0" />}
          </h4>

          {/* Description */}
          <p className="text-[11px] text-gray-500 leading-snug line-clamp-2 mb-4 flex-grow tracking-wide">
            {app.description}
          </p>

          {/* Rating + Open */}
          <div className="flex items-center justify-between mt-auto mb-3">
            <div className="flex items-center gap-2">
              <div
                className="cursor-pointer"
                onClick={(e) => {
                  e.stopPropagation();
                  if (!user || user.email === 'guest@web3central.internal') { navigate('/login'); } else { setSelectedTool(app); }
                }}
              >
                <StarRating value={rating} count={app.ratingCount} />
              </div>
            </div>
            <div onClick={(e) => e.stopPropagation()}>
              {app.isOffline ? (
                <span className="flex items-center justify-center bg-red-50 text-red-500 px-4 py-1.5 rounded-full text-[11px] font-bold border border-red-100 cursor-not-allowed">
                  Site Down
                </span>
              ) : (
                <SafeLink
                  url={app.url}
                  verified={false}
                  hideDomain={true}
                  toolId={app.id || app._id}
                  currentCount={app.clickCount}
                  className="flex items-center justify-center bg-[#6d39ff] text-white px-4 py-1.5 rounded-full text-[11px] font-bold hover:bg-[#5b2fff] transition-all whitespace-nowrap border border-[#6d39ff]/10 shadow-sm hover:shadow-md"
                >
                  Open
                </SafeLink>
              )}
            </div>
          </div>

          {/* Request to Join DAO */}
          {categoryKey === 'dao' && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (!user || user.email === 'guest@web3central.internal') {
                  navigate('/login?redirect=' + encodeURIComponent(location.pathname));
                  return;
                }
                setDaoApplicationTarget(app);
              }}
              className="w-full text-[11px] font-bold uppercase tracking-wider py-1.5 rounded-xl bg-purple-50 text-purple-700 hover:bg-purple-100 transition-all border border-purple-200/50 mb-2 flex items-center justify-center gap-1.5"
            >
              <Users size={12} /> Request to Join
            </button>
          )}

          {/* Compare bench toggle */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              toggleBench(app._id || app.id);
            }}
            className={`w-full text-[11px] font-bold uppercase tracking-wider py-1.5 rounded-xl border-2 transition-all flex items-center justify-center gap-1 ${bench.includes(String(app._id || app.id))
              ? 'bg-indigo-600 border-indigo-600 text-white shadow-md shadow-indigo-200'
              : 'bg-white border-gray-200 text-gray-500 hover:border-gray-300 hover:text-gray-700 hover:bg-gray-50'}`}
          >
            {bench.includes(String(app._id || app.id)) ? <><Check size={11} />Added</> : <><Plus size={11} />Compare</>}
          </button>
        </div>
      </motion.div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white pt-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {Array.from({ length: 8 }).map((_, i) => <CardSkeleton key={i} />)}
        </div>
      </div>
    );
  }


  // ── Coming Soon Gate ──
  if (isComingSoon) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        {/* Header */}
        <div className="pt-4 pb-4 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto w-full">
          <div className="flex items-center gap-3">
            <Link to="/apps" className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors">
              <ChevronLeft size={18} />
            </Link>
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold tracking-tight text-gray-900 leading-none">{title}</h1>
              <p className="text-xs text-gray-400 mt-0.5">{description}</p>
            </div>
          </div>
        </div>
        {/* Coming Soon Body — Animated */}
        <div className="flex-1 flex flex-col items-center justify-center px-6 py-20 text-center relative overflow-hidden">

          {/* Animated background orbs */}
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
            className="absolute w-[400px] h-[400px] pointer-events-none"
          >
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-3 h-3 rounded-full bg-purple-400/40 blur-[1px]" />
            <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-2 h-2 rounded-full bg-indigo-400/30 blur-[1px]" />
            <div className="absolute top-1/2 left-0 -translate-y-1/2 w-2.5 h-2.5 rounded-full bg-fuchsia-400/30 blur-[1px]" />
          </motion.div>
          <motion.div
            animate={{ rotate: -360 }}
            transition={{ duration: 30, repeat: Infinity, ease: 'linear' }}
            className="absolute w-[300px] h-[300px] pointer-events-none"
          >
            <div className="absolute top-0 right-0 w-2 h-2 rounded-full bg-cyan-400/40 blur-[1px]" />
            <div className="absolute bottom-0 left-0 w-2 h-2 rounded-full bg-violet-400/30 blur-[1px]" />
          </motion.div>

          {/* Pulsing glow background */}
          <motion.div
            animate={{ scale: [1, 1.2, 1], opacity: [0.15, 0.25, 0.15] }}
            transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
            className="absolute w-72 h-72 bg-gradient-to-br from-purple-400 to-indigo-500 rounded-full blur-[80px] pointer-events-none"
          />

          {/* Icon */}
          <motion.div
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="relative z-10 mb-8"
          >
            <motion.div
              animate={{ y: [0, -8, 0] }}
              transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
              className="w-24 h-24 rounded-[28px] bg-gradient-to-br from-purple-100 to-indigo-100 border border-purple-200/50 flex items-center justify-center shadow-xl shadow-purple-200/30"
            >
              <motion.div
                animate={{ rotate: [0, 10, -10, 0] }}
                transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-purple-500">
                  <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
                </svg>
              </motion.div>
            </motion.div>
          </motion.div>

          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.5 }}
            className="relative z-10 inline-flex items-center gap-2 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 text-amber-700 text-xs font-bold px-5 py-2.5 rounded-full mb-6 uppercase tracking-widest shadow-sm"
          >
            <motion.span
              animate={{ scale: [1, 1.4, 1], opacity: [1, 0.6, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
              className="w-2 h-2 rounded-full bg-amber-500"
            />
            Coming Soon
          </motion.div>

          {/* Title */}
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.6 }}
            className="relative z-10 text-4xl md:text-5xl font-black text-gray-900 mb-4 tracking-tight"
          >
            {title}
          </motion.h2>

          {/* Description */}
          <motion.p
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.5 }}
            className="relative z-10 text-gray-500 text-base max-w-lg leading-relaxed mb-10"
          >
            We're carefully curating the best <strong className="text-gray-700">{title}</strong> protocols for Web3Central.
            Be the first to know — or submit a protocol yourself.
          </motion.p>

          {/* CTAs */}
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.5 }}
            className="relative z-10 flex flex-col sm:flex-row items-center gap-3"
          >
            <Link
              to="/submit-tool"
              className="px-8 py-3.5 bg-purple-600 text-white rounded-xl font-bold text-sm hover:bg-purple-700 transition-all shadow-lg shadow-purple-600/20 hover:scale-105 hover:-translate-y-0.5"
            >
              Submit a Protocol
            </Link>
            <Link
              to="/apps"
              className="px-8 py-3.5 border-2 border-gray-200 text-gray-600 rounded-xl font-bold text-sm hover:bg-gray-50 hover:border-gray-300 transition-all"
            >
              Browse Other Categories
            </Link>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white min-h-screen text-gray-900">

      {/* ── Section 1: Category Header (Left-aligned description right under heading) ── */}
      <div className="pt-6 pb-4 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-start justify-between gap-6 pb-6 border-b border-gray-100 mb-6">
          <div className="flex items-start gap-3.5 max-w-4xl">
            <Link to="/apps" className="w-9 h-9 flex items-center justify-center rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors mt-0.5 shrink-0">
              <ChevronLeft size={18} />
            </Link>
            <div className="space-y-2 text-left">
              <h1 className="text-2xl sm:text-3xl font-black tracking-tight text-gray-900 leading-none">{title}</h1>
              <p className="text-sm sm:text-base text-gray-600 font-medium leading-relaxed">
                {description}
              </p>
              
              {/* Left-aligned Stat Pills */}
              <div className="flex flex-wrap items-center gap-2.5 pt-1">
                <div className="inline-flex items-center gap-1.5 bg-purple-50 text-purple-700 px-3 py-1 rounded-full text-xs font-bold border border-purple-100">
                  <span className="w-2 h-2 rounded-full bg-purple-500" />
                  <span><strong className="text-purple-900 font-black">{data.length}</strong> Active Protocols</span>
                </div>
                <div className="inline-flex items-center gap-1.5 bg-emerald-50 text-emerald-700 px-3 py-1 rounded-full text-xs font-bold border border-emerald-100">
                  <ShieldCheck size={13} className="text-emerald-500" />
                  <span><strong className="text-emerald-900 font-black">{data.filter(t => t.verified).length}</strong> Verified</span>
                </div>
                <div className="inline-flex items-center gap-1.5 bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-xs font-bold border border-blue-100">
                  <Share2 size={12} className="text-blue-500" />
                  <span><strong className="text-blue-900 font-black">{new Set(data.flatMap(t => t.metrics?.chains || [])).size || 1}</strong> Supported Chains</span>
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-4 shrink-0">
            <ChainFilter />
            {/* Bench bar — inline on desktop */}
            {bench.length > 0 && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="hidden md:flex items-center gap-3 px-4 py-2 bg-indigo-600 rounded-2xl text-white shadow-lg"
              >
                <span className="text-sm font-bold">{bench.length} selected</span>
                <Link
                  to={`/tool-comparison?tools=${bench.join(',')}`}
                  className="px-4 py-1.5 bg-white text-indigo-600 text-xs font-black rounded-xl uppercase tracking-wider"
                >
                  Analyze →
                </Link>
              </motion.div>
            )}
          </div>
        </div>

        {/* Bench bar — full width on mobile */}
        {bench.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="md:hidden mt-3 flex items-center justify-between p-3 bg-indigo-600 rounded-2xl text-white shadow-lg"
          >
            <span className="text-sm font-bold">{bench.length} protocol{bench.length > 1 ? 's' : ''} selected</span>
            <Link
              to={`/tool-comparison?tools=${bench.join(',')}`}
              className="px-4 py-1.5 bg-white text-indigo-600 text-xs font-black rounded-xl uppercase tracking-wider"
            >
              Analyze →
            </Link>
          </motion.div>
        )}
      </div>

      {/* ── Section 2: Search + Filter Pills ── */}
      <div className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto mb-6 space-y-3">

        {/* Trading Sub-Filter (DEX / Perps) */}
        {categoryKey === 'trading' && (
          <div className="flex gap-2">
            {[
              { label: 'All Trading', value: 'all' },
              { label: 'DEX', value: 'dex' },
              { label: 'Perps', value: 'perps' },
            ].map(sf => (
              <button
                key={sf.value}
                onClick={() => setTradingSubFilter(sf.value)}
                className={`px-5 py-2 rounded-full text-sm font-bold transition-all border ${
                  tradingSubFilter === sf.value
                    ? 'bg-purple-600 text-white border-purple-600 shadow-md shadow-purple-200'
                    : 'bg-white text-gray-600 border-gray-200 hover:border-purple-300 hover:text-purple-600'
                }`}
              >
                {sf.label}
              </button>
            ))}
          </div>
        )}

        {/* Network Scope Selector */}
        <div className="flex gap-1 p-1 bg-gray-50 border border-gray-100 rounded-2xl w-fit">
          {[
            { label: '🌐 All Networks', value: 'all' },
            { label: '⚡ Mainnet', value: 'mainnet' },
            { label: '🧪 Testnet', value: 'testnet' },
          ].map(ns => (
            <button
              key={ns.value}
              onClick={() => setNetworkScope(ns.value)}
              className={`px-4 py-2 rounded-xl text-xs font-bold transition-all ${
                networkScope === ns.value
                  ? 'bg-white text-gray-900 shadow-sm border border-gray-200'
                  : 'text-gray-500 hover:text-gray-900 border border-transparent'
              }`}
            >
              {ns.label}
            </button>
          ))}
        </div>

        <div className="w-full">
          {/* Search */}
          <div className="relative w-full">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
            <input
              type="text"
              placeholder={`Search ${title?.toLowerCase() || 'protocols'}...`}
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="w-full pl-11 pr-4 py-3 bg-gray-50 border border-gray-100 rounded-2xl text-sm text-gray-900 placeholder:text-gray-400 font-normal focus:border-purple-300 focus:ring-2 focus:ring-purple-50 outline-none transition-all"
            />
            {searchQuery && (
              <button onClick={() => setSearchQuery('')} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                <X size={15} />
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto pb-24">

        {/* ── Filtered / Search results ── */}
        {isFiltered ? (
          filteredData.length === 0 ? (
            <div className="text-center py-20">
              <Search size={40} className="mx-auto text-gray-200 mb-4" />
              <p className="font-bold text-gray-900 mb-1">No protocols found</p>
              <p className="text-sm text-gray-400 mb-4">Try a different search or filter</p>
              <button
                onClick={() => { setSearchQuery(''); setSortBy('default'); setChainFilter('all'); }}
                className="px-4 py-2 bg-purple-50 text-purple-600 rounded-xl text-sm font-bold hover:bg-purple-100 transition-colors"
              >
                Clear filters
              </button>
            </div>
          ) : (
            <motion.div variants={containerVariants} initial="hidden" animate="visible" className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              <AnimatePresence mode="popLayout">
                {filteredData.map(app => <ProtocolCard key={app._id} app={app} />)}
              </AnimatePresence>
            </motion.div>
          )
        ) : (
          <>
            {/* ── Section 3: Featured Hero Card ── */}
            {featured && (
              <motion.div variants={fadeUp} initial="hidden" animate="visible" custom={0} className="mb-8">
                <div className="relative rounded-2xl overflow-hidden bg-gradient-to-br from-indigo-900 via-purple-800 to-indigo-700 p-6 md:p-8 shadow-xl">
                  <div className="absolute inset-0 bg-gradient-to-br from-purple-600/20 to-transparent" />
                  {/* Verified tag — top-right corner */}
                  {featured.verified && (
                    <span className="absolute top-4 right-4 z-20 flex items-center gap-1 text-xs text-emerald-300 font-bold bg-black/20 backdrop-blur-sm px-2.5 py-1 rounded-full">
                      <ShieldCheck size={13} /> Verified
                    </span>
                  )}
                  <div className="relative z-10 flex items-center gap-5">
                    <div className="w-20 h-20 md:w-24 md:h-24 rounded-2xl bg-white/10 backdrop-blur-sm border border-white/20 p-3 shrink-0 shadow-lg">
                      <ToolLogo tool={featured} />
                    </div>
                    <div className="flex-grow min-w-0">
                      <p className="text-purple-200 text-[10px] font-bold uppercase tracking-widest mb-1">Featured Protocol</p>
                      <h2 className="text-white font-bold text-2xl md:text-3xl leading-none mb-2">{featured.name}</h2>
                      <p className="text-purple-200 text-sm leading-relaxed line-clamp-2 max-w-2xl">{featured.description}</p>
                    </div>
                  </div>
                  <div className="relative z-10 grid grid-cols-2 md:flex md:flex-row md:justify-end items-stretch md:items-center gap-3 mt-5">
                    <button
                      onClick={() => toggleBench(featured._id || featured.id)}
                      className={`w-full md:w-auto px-3 md:px-5 py-2.5 text-sm font-bold rounded-full border border-white/30 transition-all ${bench.includes(String(featured._id || featured.id)) ? 'bg-white/20 text-white' : 'text-white/80 hover:bg-white/10'}`}
                    >
                      {bench.includes(String(featured._id || featured.id)) ? '✓ Compare' : '+ Compare'}
                    </button>
                    {featured.isOffline ? (
                      <span className="w-full md:w-auto flex items-center justify-center px-4 md:px-6 py-2.5 bg-red-500 text-white text-sm font-black rounded-full cursor-not-allowed shadow-lg shadow-red-500/20">
                        Site Down
                      </span>
                    ) : (
                      <SafeLink
                        url={featured.url}
                        verified={false}
                        hideDomain={true}
                        toolId={featured.id || featured._id}
                        currentCount={featured.clickCount}
                        className="w-full md:w-auto flex items-center justify-center px-4 md:px-6 py-2.5 bg-white text-gray-900 text-sm font-black rounded-full hover:bg-gray-100 transition-colors"
                      >
                        Open
                      </SafeLink>
                    )}
                  </div>
                </div>
              </motion.div>
            )}

            {/* ── Section 4: Trending ── */}
            {trending.length > 0 && (
              <motion.div variants={fadeUp} initial="hidden" animate="visible" custom={0.1} className="mb-8">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-bold text-gray-900">Trending</h2>
                  <button onClick={() => setSortBy('tvl')} className="text-sm text-purple-600 font-semibold hover:underline">See all</button>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {trending.map(app => <ProtocolCard key={app._id} app={app} />)}
                </div>
              </motion.div>
            )}

            {/* ── Section 5: New Protocols ── */}
            {newProtocols.length > 0 && (
              <motion.div variants={fadeUp} initial="hidden" animate="visible" custom={0.2}>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-bold text-gray-900">New Protocols</h2>
                  <span className="text-xs text-gray-400 font-medium">{newProtocols.length} listed</span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {newProtocols.map(app => <ProtocolCard key={app._id} app={app} />)}
                </div>
              </motion.div>
            )}

            {data.length === 0 && (
              <div className="text-center py-24">
                <LayoutGrid size={40} className="mx-auto text-gray-200 mb-4" />
                <p className="font-bold text-gray-900 mb-1">No protocols listed yet</p>
                <p className="text-sm text-gray-400">Check back soon!</p>
              </div>
            )}
          </>
        )}

        {/* Submit CTA */}
        <div className="mt-16 p-8 bg-gray-900 rounded-2xl text-center relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-purple-600/20 via-transparent to-indigo-600/20 pointer-events-none" />
          <h3 className="text-xl font-bold text-white mb-2 relative z-10">Submit a Protocol</h3>
          <p className="text-gray-400 text-sm mb-5 relative z-10">Help build the most comprehensive Web3 directory.</p>
          <Link to="/submit-tool" className="relative z-10 inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-purple-600 text-white text-sm font-bold hover:bg-purple-500 transition-all">
            Submit Protocol <Zap size={14} />
          </Link>
        </div>
      </div>

      {/* Metrics Side Panel */}
      <MetricsPanel 
        isOpen={!!selectedMetricsProtocol}
        protocol={selectedMetricsProtocol}
        onClose={() => setSelectedMetricsProtocol(null)}
      />

      {/* Rating Modal */}
      {selectedTool && (
        <RatingModal
          tool={selectedTool}
          onClose={() => setSelectedTool(null)}
          onRatingSubmitted={(newAvg) => {
            setData(prev => prev.map(t => t.id === selectedTool.id ? { ...t, rating: newAvg } : t));
          }}
        />
      )}

      {/* DAO Application Modal */}
      {daoApplicationTarget && (
        <DaoApplicationModal
          dao={daoApplicationTarget}
          onClose={() => setDaoApplicationTarget(null)}
        />
      )}
    </div>
  );
}

/* ─── DAO Application Modal ─── */
const DaoApplicationModal = ({ dao, onClose }) => {
  const [talent, setTalent] = useState("developer");
  const [valueAdd, setValueAdd] = useState("");
  const [portfolio, setPortfolio] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (valueAdd.trim().length < 10) {
      setError("Please describe the value you bring in at least 10 characters.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      const API_BASE = window.location.hostname === "localhost" ? "http://localhost:5000/api" : "/api";
      const res = await fetch(`${API_BASE}/tools/daos/${dao._id || dao.id}/apply`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`
        },
        body: JSON.stringify({ talent, valueAdd, portfolio })
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || data.message || "Failed to submit application");
      }
      setSuccess(true);
      setTimeout(() => onClose(), 1500);
    } catch (err) {
      setError(err.message || "Failed to submit application.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-3xl border border-gray-150 shadow-2xl max-w-md w-full p-6 text-left relative"
      >
        <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-gray-900 transition-colors">
          <X size={18} />
        </button>

        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-xl bg-purple-500/10 border border-purple-500/20 flex items-center justify-center text-purple-600 font-bold shrink-0">
            🏛️
          </div>
          <div>
            <h3 className="text-lg font-black text-gray-900 leading-tight">Apply to {dao.name}</h3>
            <p className="text-xs text-gray-400 font-semibold uppercase tracking-wider mt-0.5">Talent Registry & Onboarding</p>
          </div>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-100 text-red-600 rounded-xl text-xs font-semibold">
            {error}
          </div>
        )}

        {success ? (
          <div className="py-8 text-center space-y-3">
            <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-600 text-2xl mx-auto">
              ✓
            </div>
            <h4 className="text-base font-bold text-gray-900">Application Submitted!</h4>
            <p className="text-xs text-gray-400">The DAO maintainers will review your profile and contact you soon.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-bold text-gray-600 mb-1.5 uppercase tracking-widest">Select Your Talent</label>
              <select
                value={talent}
                onChange={e => setTalent(e.target.value)}
                className="w-full px-4 py-2.5 bg-gray-50 border-2 border-gray-100 rounded-xl text-sm focus:border-purple-400 focus:outline-none transition-colors font-medium text-gray-750"
              >
                <option value="developer">Developer (Smart Contracts, Frontend, Infra)</option>
                <option value="designer">Designer (UI/UX, Brand, Media)</option>
                <option value="marketing">Marketing (Growth, Copywriting, SEO)</option>
                <option value="community">Community (Moderation, Events, Support)</option>
                <option value="research">Research (Tokenomics, Data Science, Writing)</option>
                <option value="operations">Operations (Treasury, Legal, Management)</option>
                <option value="other">Other Unique Value Add</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-600 mb-1.5 uppercase tracking-widest">Value-Add & Experience</label>
              <textarea
                value={valueAdd}
                onChange={e => setValueAdd(e.target.value)}
                placeholder="Briefly describe what value you can add to the DAO and why you'd like to join..."
                rows={4}
                maxLength={500}
                className="w-full px-4 py-3 border-2 border-gray-100 rounded-xl text-sm focus:border-purple-400 focus:outline-none transition-colors resize-none font-medium text-gray-750"
              />
              <p className="text-[10px] text-gray-300 mt-1 text-right">{valueAdd.length}/500</p>
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-600 mb-1.5 uppercase tracking-widest">Portfolio or Profile Link (Optional)</label>
              <input
                type="url"
                value={portfolio}
                onChange={e => setPortfolio(e.target.value)}
                placeholder="https://github.com/username or portfolio link"
                className="w-full px-4 py-2.5 border-2 border-gray-100 rounded-xl text-sm focus:border-purple-400 focus:outline-none transition-colors font-medium text-gray-750"
              />
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="w-full py-3.5 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white font-bold rounded-xl text-sm transition-all shadow-md shadow-purple-200 disabled:opacity-50"
            >
              {submitting ? "Submitting Application..." : "Submit Join Request"}
            </button>
          </form>
        )}
      </motion.div>
    </div>
  );
};
